<template>
  <div class="w-full h-full flex flex-col px-4 sm:px-6 lg:px-8 pt-2 md:pt-4 pb-4 md:pb-8 overflow-y-auto overflow-x-hidden">
    <ArchivedCohortBanner v-if="cohort.archived_at" />

    <!-- Controls & Filters Container (2-tier on mobile, 1-row on lg desktop) -->
    <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between w-full shrink-0 gap-3 lg:gap-0 mb-4">
      <!-- Tier 2 on mobile, Left side on desktop: Filter Pills -->
      <div class="order-2 lg:order-1 flex flex-row gap-2 sm:gap-[12px] items-center overflow-x-auto hide-scrollbar w-full lg:w-auto py-1 -mx-4 px-4 sm:mx-0 sm:px-0">
        <!-- Filter Pills -->
        <button
          type="button"
          :class="pillClass('all')"
          @click="setFilter('all')"
        >
          <div
            class="text-[13px] sm:text-[14px] font-['Raleway',sans-serif] text-left whitespace-nowrap"
            :class="activeFilter === 'all' ? 'text-black font-semibold' : 'text-black font-normal'"
          >
            All programmes
          </div>
        </button>

        <button
          type="button"
          :class="pillClass('drafts')"
          @click="setFilter('drafts')"
        >
          <div
            class="text-[13px] sm:text-[14px] font-['Raleway',sans-serif] text-left whitespace-nowrap"
            :class="(activeFilter === 'drafts' || activeFilter === 'draft') ? 'text-black font-semibold' : 'text-black font-normal'"
          >
            Drafts
          </div>
        </button>

        <button
          type="button"
          :class="pillClass('in_progress')"
          @click="setFilter('in_progress')"
        >
          <div
            class="text-[13px] sm:text-[14px] font-['Raleway',sans-serif] text-left whitespace-nowrap"
            :class="activeFilter === 'in_progress' ? 'text-black font-semibold' : 'text-black font-normal'"
          >
            In progress
          </div>
        </button>

        <button
          type="button"
          :class="pillClass('completed')"
          @click="setFilter('completed')"
        >
          <div
            class="text-[13px] sm:text-[14px] font-['Raleway',sans-serif] text-left whitespace-nowrap"
            :class="activeFilter === 'completed' ? 'text-black font-semibold' : 'text-black font-normal'"
          >
            Completed
          </div>
        </button>
      </div>

      <!-- Tier 1 on mobile, Right side on desktop: Search + Filter Icon + Add Programme -->
      <div class="order-1 lg:order-2 flex items-center gap-2 sm:gap-3 w-full lg:w-auto">
        <!-- Search input expanding on mobile, fixed 216px on desktop -->
        <div class="flex-1 lg:flex-initial lg:w-[216px] h-[40px] lg:h-[43px] bg-white rounded-full px-3.5 sm:px-[16px] flex items-center gap-[9px] shadow-none border border-transparent focus-within:border-[#01d680] transition-all min-w-0">
          <button
            type="button"
            class="search-icon-btn p-0 m-0 bg-transparent border-none outline-none flex items-center justify-center shrink-0"
            :class="searchQuery ? 'cursor-pointer' : 'cursor-default'"
            tabindex="-1"
            @click="clearSearch"
          >
            <svg
              v-if="!searchQuery"
              width="14"
              height="14"
              viewBox="0 0 18 18"
              fill="none"
              class="text-[#5b5c60] shrink-0"
            >
              <path
                d="M14 14l4 4m-18-10c0 2.12173 0.84285 4.15656 2.34315 5.65686 1.50029 1.50029 3.53512 2.34314 5.65685 2.34314 2.12173 0 4.15656-0.84285 5.65685-2.34314 1.50029-1.50029 2.34314-3.53512 2.34315-5.65686 0-2.12173-0.84285-4.15656-2.34315-5.65685-1.50029-1.50029-3.53512-2.34315-5.65685-2.34315-2.12173 0-4.15656 0.84285-5.65685 2.34315-1.50029 1.50029-2.34315 3.53512-2.34315 5.65685z"
                stroke="currentColor"
                stroke-width="1.1"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <svg
              v-else
              class="w-[13px] h-[13px] text-[#5b5c60] hover:text-[#043f42] transition-colors cursor-pointer"
              viewBox="0 0 14 14"
              fill="none"
            >
              <path
                d="M1.5 1.5L12.5 12.5M1.5 12.5L12.5 1.5"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search programmes"
            class="bg-transparent border-none outline-none text-[#000000] text-[13px] font-['Raleway',sans-serif] font-normal w-full placeholder:text-[#5b5c60] m-0 p-0 shadow-none focus:ring-0"
            autocomplete="off"
            @input="currentPage = 1"
          >
        </div>

        <!-- Preferences / Filter & Sort Button -->
        <div class="relative shrink-0">
          <button
            type="button"
            aria-label="Filter and sort programmes"
            :aria-expanded="isFilterSortOpen"
            class="w-[40px] lg:w-[43px] h-[40px] lg:h-[43px] bg-white rounded-full flex items-center justify-center shadow-none cursor-pointer hover:bg-gray-50 border border-transparent focus-visible:ring-2 focus-visible:ring-[#01d680] transition-colors shrink-0"
            :class="{ 'border-[#01d680] text-[#01d680]': isFilterSortOpen || currentSort !== 'date-desc' }"
            @click.stop="toggleFilterSort"
          >
            <svg
              viewBox="0 0 18.5 17.5"
              fill="none"
              class="w-[18px] sm:w-[19px] h-[17px] sm:h-[18px]"
            >
              <path
                d="M18.5 8.787l-12.355 0m-4.361 0l-1.784 0m18.5 6.607l-5.748 0m-4.361 0l-8.391 0m18.5-13.214l-3.105 0m-4.361 0l-11.034 0m13.214 2.18c0.28628 0 0.56976-0.05639 0.83425-0.16594 0.26449-0.10956 0.50481-0.27013 0.70724-0.47257 0.20243-0.20243 0.36301-0.44275 0.47257-0.70724 0.10955-0.26449 0.16594-0.54797 0.16594-0.83425 0-0.28628-0.05639-0.56976-0.16594-0.83425-0.10956-0.26449-0.27013-0.50481-0.47257-0.70724-0.20243-0.20243-0.44275-0.36301-0.70724-0.47257-0.26449-0.10956-0.54797-0.16594-0.83425-0.16594-0.57817 0-1.13266 0.22968-1.54149 0.63851-0.40883 0.40883-0.63851 0.96332-0.63851 1.54149 0 0.57817 0.22968 1.13266 0.63851 1.54149 0.40883 0.40883 0.96332 0.63851 1.54149 0.63851z m-9.25 6.607c0.57817 0 1.13266-0.22968 1.54149-0.63851 0.40883-0.40883 0.63851-0.96332 0.63851-1.54149 0-0.57817-0.22968-1.13266-0.63851-1.54149-0.40883-0.40883-0.96332-0.63851-1.54149-0.63851-0.57817 0-1.13266 0.22968-1.54149 0.63851-0.40883 0.40883-0.63851 0.96332-0.63851 1.54149 0 0.57817 0.22968 1.13266 0.63851 1.54149 0.40883 0.40883 0.96332 0.63851 1.54149 0.63851z m6.607 6.608c0.5783 0 1.13292-0.22973 1.54185-0.63865 0.40892-0.40892 0.63915-0.96404 0.63915-1.54235 0-0.5783-0.23023-1.13242-0.63915-1.54135-0.40892-0.40892-0.96354-0.63865-1.54185-0.63865-0.57817 0-1.13266 0.22968-1.54149 0.63851-0.40883 0.40883-0.63851 0.96332-0.63851 1.54149 0 0.57817 0.22968 1.13267 0.63851 1.54149 0.40883 0.40883 0.96332 0.63951 1.54149 0.63951z"
                stroke="#000000"
                stroke-width="1.564"
                stroke-linecap="round"
              />
            </svg>
          </button>
          <div
            v-if="isFilterSortOpen"
            class="absolute top-[44px] right-0 w-[210px] bg-white shadow-2xl rounded-[14px] z-[100] flex flex-col py-2 border border-black/10 font-['Raleway',sans-serif]"
            @click.stop
          >
            <div class="px-4 py-1.5 text-[11px] text-[#5b5c60] uppercase tracking-wider font-bold">
              Sort by
            </div>
            <button
              type="button"
              class="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex justify-between items-center cursor-pointer bg-transparent border-none"
              :class="currentSort === 'date-desc' ? 'text-[#01d680] font-bold' : 'text-gray-700'"
              @click="setSort('date-desc')"
            >
              <span>Newest First</span>
              <span v-if="currentSort === 'date-desc'">✓</span>
            </button>
            <button
              type="button"
              class="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex justify-between items-center cursor-pointer bg-transparent border-none"
              :class="currentSort === 'date-asc' ? 'text-[#01d680] font-bold' : 'text-gray-700'"
              @click="setSort('date-asc')"
            >
              <span>Oldest First</span>
              <span v-if="currentSort === 'date-asc'">✓</span>
            </button>
            <button
              type="button"
              class="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex justify-between items-center cursor-pointer bg-transparent border-none"
              :class="currentSort === 'name-asc' ? 'text-[#01d680] font-bold' : 'text-gray-700'"
              @click="setSort('name-asc')"
            >
              <span>Name (A – Z)</span>
              <span v-if="currentSort === 'name-asc'">✓</span>
            </button>
            <button
              type="button"
              class="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex justify-between items-center cursor-pointer bg-transparent border-none"
              :class="currentSort === 'name-desc' ? 'text-[#01d680] font-bold' : 'text-gray-700'"
              @click="setSort('name-desc')"
            >
              <span>Name (Z – A)</span>
              <span v-if="currentSort === 'name-desc'">✓</span>
            </button>
          </div>
        </div>

        <!-- Add Programme Button (Desktop only, mobile uses FAB) -->
        <button
          v-if="cohort.programmes && cohort.programmes.length > 0 && !cohort.archived_at"
          class="hidden lg:flex items-center bg-[#01d680] text-[#043f42] font-['Raleway',sans-serif] text-[13px] font-bold px-[20px] py-[10px] rounded-full hover:bg-[#01b56c] transition-colors cursor-pointer whitespace-nowrap shrink-0"
          @click="openAddProgrammeModal"
        >
          Add Programme
        </button>
      </div>
    </div>

    <!-- Main Content Area -->
    <div class="flex-1 w-full relative">
      <!-- Total Empty State (Zero Programmes in Cohort) -->
      <div
        v-if="allProgrammes.length === 0"
        class="w-full h-full flex flex-col items-center justify-center pt-6 sm:pt-[50px]"
      >
        <div class="border-2 border-dashed border-[#d1d5db] rounded-[20px] sm:rounded-[25px] p-6 sm:p-[50px] flex flex-col items-center justify-center text-center min-h-[320px] sm:min-h-[400px] w-full max-w-[800px] bg-white/50">
          <div class="w-[64px] h-[64px] rounded-full bg-[#f8fff8] border border-[#22ec9b] flex items-center justify-center mb-[20px]">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              class="text-[#01d680]"
            >
              <line
                x1="12"
                y1="5"
                x2="12"
                y2="19"
              />
              <line
                x1="5"
                y1="12"
                x2="19"
                y2="12"
              />
            </svg>
          </div>
          <h3 class="text-[#043f42] font-['Raleway',sans-serif] font-bold text-[18px] sm:text-[20px] mb-[10px]">
            Configure a programme
          </h3>
          <p class="text-[#5b5c60] font-['Raleway',sans-serif] text-[14px] sm:text-[15px] mb-[30px]">
            Get started by creating your first programme inside this cohort.
          </p>
          <button
            v-if="!cohort.archived_at"
            type="button"
            class="bg-[#01d680] text-[#043f42] font-['Raleway',sans-serif] text-[15px] sm:text-[16px] font-bold px-[24px] sm:px-[30px] py-[10px] sm:py-[12px] rounded-full hover:bg-[#01b56c] transition-colors cursor-pointer"
            @click="openAddProgrammeModal"
          >
            Create Programme
          </button>
        </div>
      </div>

      <!-- Filter Empty State (Programmes exist but none match active filter/search) -->
      <div
        v-else-if="filteredProgrammes.length === 0"
        class="w-full h-full flex flex-col items-center justify-center pt-6 sm:pt-[50px]"
      >
        <div class="border-2 border-dashed border-[#d1d5db] rounded-[20px] sm:rounded-[25px] p-6 sm:p-[50px] flex flex-col items-center justify-center text-center min-h-[320px] sm:min-h-[400px] w-full max-w-[800px] bg-white/50">
          <div class="w-[64px] h-[64px] rounded-full bg-[#f8fff8] border border-[#22ec9b] flex items-center justify-center mb-[20px]">
            <svg
              class="w-[28px] h-[28px] text-[#5b5c60]"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle
                cx="11"
                cy="11"
                r="8"
              />
              <line
                x1="21"
                y1="21"
                x2="16.65"
                y2="16.65"
              />
            </svg>
          </div>
          <h3 class="text-[#043f42] font-['Raleway',sans-serif] font-bold text-[20px] mb-[10px]">
            {{ filterEmptyTitle }}
          </h3>
          <p class="text-[#5b5c60] font-['Raleway',sans-serif] text-[15px] mb-[25px]">
            {{ filterEmptySubtitle }}
          </p>
          <button
            type="button"
            class="px-5 py-2.5 rounded-full border border-black text-black font-['Raleway',sans-serif] font-bold text-sm hover:bg-black hover:text-white transition-colors cursor-pointer"
            @click="resetFilters"
          >
            Show all programmes
          </button>
        </div>
      </div>

      <!-- Programme Card Grid + Pagination (exact match to CohortsList) -->
      <div
        v-else
        class="w-full flex-1 flex flex-col justify-between mt-[36px] pb-24 lg:pb-0"
      >
        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-[22px]">
          <ProgrammeCard
            v-for="prog in paginatedProgrammes"
            :key="prog.id"
            :programme="prog"
            :cohort="cohort"
            @programme-action="onProgrammeAction"
          />
        </div>

        <!-- Pagination Bar -->
        <div
          v-if="totalPages > 1"
          class="flex items-center justify-between mt-[10px] pt-[8px] border-t border-gray-200/60 w-full shrink-0"
        >
          <div class="text-[14px] text-[#5b5c60] font-['Raleway',sans-serif]">
            Showing {{ paginationFrom }}–{{ paginationTo }} of {{ filteredProgrammes.length }} programmes
          </div>
          <div class="flex items-center gap-[12px]">
            <!-- Previous Button -->
            <a
              v-if="currentPage > 1"
              href="#"
              class="w-[32px] h-[32px] rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer text-[#000000]"
              @click.prevent="setPage(currentPage - 1)"
            >
              <svg
                class="w-[18px] h-[18px]"
                viewBox="0 0 24 24"
                fill="none"
              ><circle
                cx="12"
                cy="12"
                r="9.5"
                stroke="currentColor"
                stroke-width="1.2"
              /><path
                d="M13.5 8.5L9.5 12L13.5 15.5"
                stroke="currentColor"
                stroke-width="1.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              /></svg>
            </a>
            <div
              v-else
              class="w-[32px] h-[32px] rounded-full border border-gray-200 flex items-center justify-center text-gray-300 cursor-not-allowed opacity-40"
            >
              <svg
                class="w-[18px] h-[18px]"
                viewBox="0 0 24 24"
                fill="none"
              ><circle
                cx="12"
                cy="12"
                r="9.5"
                stroke="currentColor"
                stroke-width="1.2"
              /><path
                d="M13.5 8.5L9.5 12L13.5 15.5"
                stroke="currentColor"
                stroke-width="1.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              /></svg>
            </div>

            <!-- Page Counter -->
            <span class="text-[14px] font-semibold text-[#000000] font-['Raleway',sans-serif] px-1">Page {{ currentPage }} of {{ totalPages }}</span>

            <!-- Next Button -->
            <a
              v-if="currentPage < totalPages"
              href="#"
              class="w-[32px] h-[32px] rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer text-[#000000]"
              @click.prevent="setPage(currentPage + 1)"
            >
              <svg
                class="w-[18px] h-[18px]"
                viewBox="0 0 24 24"
                fill="none"
              ><circle
                cx="12"
                cy="12"
                r="9.5"
                stroke="currentColor"
                stroke-width="1.2"
              /><path
                d="M10.5 8.5L14.5 12L10.5 15.5"
                stroke="currentColor"
                stroke-width="1.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              /></svg>
            </a>
            <div
              v-else
              class="w-[32px] h-[32px] rounded-full border border-gray-200 flex items-center justify-center text-gray-300 cursor-not-allowed opacity-40"
            >
              <svg
                class="w-[18px] h-[18px]"
                viewBox="0 0 24 24"
                fill="none"
              ><circle
                cx="12"
                cy="12"
                r="9.5"
                stroke="currentColor"
                stroke-width="1.2"
              /><path
                d="M10.5 8.5L14.5 12L10.5 15.5"
                stroke="currentColor"
                stroke-width="1.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              /></svg>
            </div>
          </div>
        </div>
      </div>
    </div>

    <AddProgrammeModal
      :show="showAddProgrammeModal"
      :cohort-slug="String(cohort.id)"
      :cohort-seta-type="cohortSetaType"
      @close="showAddProgrammeModal = false"
      @created="onProgrammeCreated"
    />

    <!-- Delete Programme Modal -->
    <dialog
      v-if="programmeToDelete"
      :class="['modal', { 'modal-open': true }]"
      @cancel.prevent="!isDeletingProgramme && (programmeToDelete = null)"
    >
      <div class="modal-box w-[calc(100%-32px)] max-w-[480px] mx-4 sm:mx-auto p-5 sm:p-[28px] rounded-[24px] bg-white border border-[#22ec9b] relative font-['Raleway',sans-serif] shadow-2xl">
        <form method="dialog">
          <button
            class="btn btn-sm btn-circle btn-ghost absolute right-4 top-4 text-[#5b5c60]"
            :disabled="isDeletingProgramme"
            @click.prevent="!isDeletingProgramme && (programmeToDelete = null)"
          >
            ✕
          </button>
        </form>

        <!-- Header -->
        <div class="flex items-center gap-[10px] mb-[12px]">
          <div class="w-[36px] h-[36px] rounded-full bg-red-100 flex items-center justify-center shrink-0">
            <svg
              class="w-[18px] h-[18px] text-[#dc2626]"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
          </div>
          <div>
            <h3 class="font-bold text-[18px] text-[#043f42] m-0">
              {{ isProgrammeDeleteSuccess ? 'Programme Deleted' : 'Delete Programme' }}
            </h3>
            <p class="text-[12px] text-[#5b5c60] m-0">
              This action cannot be undone.
            </p>
          </div>
        </div>

        <div class="w-full h-[1px] bg-[#043f421a] my-[14px]" />

        <!-- Pre-Deletion Confirmation -->
        <div
          v-if="!isDeletingProgramme && !isProgrammeDeleteSuccess"
          class="flex flex-col gap-[8px]"
        >
          <p class="text-[14px] text-[#5b5c60] leading-relaxed m-0">
            Are you sure you want to delete <strong>{{ programmeToDelete.name || `Programme #${programmeToDelete.id}` }}</strong>?
          </p>
        </div>

        <!-- Active Progress / Success State -->
        <div
          v-if="isDeletingProgramme || isProgrammeDeleteSuccess"
          class="flex flex-col gap-[14px] py-[10px]"
        >
          <div class="flex justify-between items-center text-[13px] font-semibold text-[#043f42]">
            <span>Status</span>
            <span>{{ isProgrammeDeleteSuccess ? '100%' : `${programmeDeleteProgress}%` }}</span>
          </div>
          <div class="w-full h-[8px] bg-gray-100 rounded-full overflow-hidden">
            <div
              class="h-full transition-all duration-300 ease-out"
              :class="isProgrammeDeleteSuccess ? 'bg-[#01d680]' : 'bg-[#dc2626]'"
              :style="{ width: `${programmeDeleteProgress}%` }"
            />
          </div>

          <div class="flex items-center gap-[8px] text-[13px]">
            <svg
              v-if="!isProgrammeDeleteSuccess"
              class="animate-spin -ml-1 mr-2 h-4 w-4 text-[#dc2626] inline-block"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <svg
              v-else
              class="w-[16px] h-[16px] text-[#01d680]"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span :class="isProgrammeDeleteSuccess ? 'text-[#00721b] font-bold' : 'text-[#5b5c60] font-medium'">
              {{ programmeDeleteStatusText }}
            </span>
          </div>
        </div>

        <!-- Error Message -->
        <div
          v-if="deleteProgrammeError"
          class="text-red-600 text-[13px] mt-[12px] p-[10px] bg-red-50 rounded-[10px] border border-red-200"
        >
          {{ deleteProgrammeError }}
        </div>

        <!-- Action Buttons -->
        <div class="modal-action mt-[20px]">
          <button
            v-if="!isProgrammeDeleteSuccess"
            type="button"
            class="btn btn-ghost rounded-[38px] text-black font-['Raleway',sans-serif] font-bold"
            :disabled="isDeletingProgramme"
            @click.prevent="programmeToDelete = null"
          >
            Cancel
          </button>
          <button
            v-if="!isProgrammeDeleteSuccess"
            type="button"
            class="btn rounded-[38px] bg-[#dc2626] hover:bg-red-700 border-none text-white font-['Raleway',sans-serif] font-bold"
            :disabled="isDeletingProgramme"
            @click.prevent="submitDeleteProgramme"
          >
            <span
              v-if="isDeletingProgramme"
              class="loading loading-spinner loading-sm"
            />
            {{ isDeletingProgramme ? 'Deleting...' : 'Delete' }}
          </button>
          <button
            v-if="isProgrammeDeleteSuccess"
            type="button"
            class="btn rounded-[38px] bg-[#01d680] hover:bg-[#00c270] border-none text-[#043f42] font-['Raleway',sans-serif] font-bold w-full"
            @click.prevent="finishProgrammeDelete"
          >
            Done
          </button>
        </div>
      </div>
      <form
        method="dialog"
        class="modal-backdrop bg-[#00000099]"
      >
        <button
          :disabled="isDeletingProgramme"
          @click.prevent="!isDeletingProgramme && (programmeToDelete = null)"
        >
          close
        </button>
      </form>
    </dialog>

    <!-- Clone Programme Modal -->
    <dialog
      v-if="programmeToClone"
      id="clone_programme_modal"
      :class="['modal', { 'modal-open': true }]"
      @cancel.prevent="!isCloningProgramme && (programmeToClone = null)"
    >
      <div
        class="modal-box w-[calc(100%-32px)] max-w-[480px] mx-4 sm:mx-auto p-5 sm:p-[28px] rounded-[24px] bg-white border border-[#22ec9b] relative shadow-2xl"
      >
        <form method="dialog">
          <button
            class="btn btn-sm btn-circle btn-ghost absolute right-4 top-4 text-[#5b5c60]"
            :disabled="isCloningProgramme"
            @click.prevent="!isCloningProgramme && (programmeToClone = null)"
          >
            ✕
          </button>
        </form>

        <!-- Header -->
        <div class="flex items-center gap-[10px] mb-[12px]">
          <div class="w-[36px] h-[36px] rounded-full bg-[#f8fff8] border border-[#22ec9b] flex items-center justify-center shrink-0">
            <svg
              class="w-[18px] h-[18px] text-[#01d680]"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect
                x="9"
                y="9"
                width="13"
                height="13"
                rx="2"
                ry="2"
              />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
          </div>
          <div>
            <h3 class="font-bold text-[18px] text-[#043f42] m-0">
              Clone Programme
            </h3>
          </div>
        </div>

        <div class="w-full h-[1px] bg-[#043f421a] my-[14px]" />

        <form @submit.prevent="submitCloneProgramme">
          <div class="mb-4">
            <label class="block text-sm font-bold text-[#043f42] font-['Raleway',sans-serif] mb-2">New Programme Name</label>
            <input
              v-model="cloneProgrammeName"
              type="text"
              required
              class="w-full h-11 px-4 border border-gray-300 rounded-[12px] focus:outline-none focus:border-[#01d680] focus:ring-2 focus:ring-[#01d680]/20 font-['Raleway',sans-serif] text-sm"
              placeholder="Programme name"
            >
          </div>

          <p class="text-[13px] text-[#5b5c60] mb-4">
            Duplicate <strong>{{ programmeToClone.name || `Programme #${programmeToClone.id}` }}</strong> and its settings.
          </p>

          <div
            v-if="cloneProgrammeError"
            class="text-red-600 text-[13px] mb-4 p-[10px] bg-red-50 rounded-[10px] border border-red-200"
          >
            {{ cloneProgrammeError }}
          </div>

          <div class="modal-action mt-[20px] flex items-center justify-end gap-3">
            <button
              type="button"
              class="px-5 py-2.5 rounded-full border border-gray-300 text-gray-700 font-['Raleway',sans-serif] font-bold text-sm hover:bg-gray-50 transition-colors cursor-pointer bg-white"
              :disabled="isCloningProgramme"
              @click.prevent="programmeToClone = null"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="flex items-center justify-center px-5 py-2.5 rounded-full bg-[#01d680] text-[#043f42] hover:bg-[#00c270] font-['Raleway',sans-serif] font-bold text-sm transition-colors border-none cursor-pointer shadow-sm disabled:opacity-50"
              :disabled="isCloningProgramme"
            >
              <span
                v-if="isCloningProgramme"
                class="w-4 h-4 mr-2 border-2 border-[#043f42] border-t-transparent rounded-full animate-spin"
              />
              {{ isCloningProgramme ? 'Cloning...' : 'Clone Programme' }}
            </button>
          </div>
        </form>
      </div>
      <form
        method="dialog"
        class="modal-backdrop bg-[#00000099]"
      >
        <button
          :disabled="isCloningProgramme"
          @click.prevent="!isCloningProgramme && (programmeToClone = null)"
        >
          close
        </button>
      </form>
    </dialog>

    <!-- Archive Programme Modal -->
    <dialog
      v-if="programmeToArchive"
      id="archive_programme_modal"
      :class="['modal', { 'modal-open': true }]"
      @cancel.prevent="!isArchivingProgramme && (programmeToArchive = null)"
    >
      <div
        class="modal-box w-[calc(100%-32px)] max-w-[480px] mx-4 sm:mx-auto p-5 sm:p-[28px] rounded-[24px] bg-white border border-[#22ec9b] relative shadow-2xl"
      >
        <form method="dialog">
          <button
            class="btn btn-sm btn-circle btn-ghost absolute right-4 top-4 text-[#5b5c60]"
            :disabled="isArchivingProgramme"
            @click.prevent="!isArchivingProgramme && (programmeToArchive = null)"
          >
            ✕
          </button>
        </form>

        <!-- Header -->
        <div class="flex items-center gap-[10px] mb-[12px]">
          <div class="w-[36px] h-[36px] rounded-full bg-[#f8fff8] border border-[#22ec9b] flex items-center justify-center shrink-0">
            <svg
              class="w-[18px] h-[18px] text-[#01d680]"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="21 8 21 21 3 21 3 8" />
              <rect
                x="1"
                y="3"
                width="22"
                height="5"
              />
              <line
                x1="10"
                y1="12"
                x2="14"
                y2="12"
              />
            </svg>
          </div>
          <div>
            <h3 class="font-bold text-[18px] text-[#043f42] m-0">
              Archive Programme
            </h3>
          </div>
        </div>

        <div class="w-full h-[1px] bg-[#043f421a] my-[14px]" />

        <p class="text-[14px] text-[#5b5c60] leading-relaxed mb-4">
          Archive <strong>{{ programmeToArchive.name || `Programme #${programmeToArchive.id}` }}</strong>? It will be kept for audit records.
        </p>

        <div
          v-if="archiveProgrammeError"
          class="text-red-600 text-[13px] mb-4 p-[10px] bg-red-50 rounded-[10px] border border-red-200"
        >
          {{ archiveProgrammeError }}
        </div>

        <div class="modal-action mt-[20px] flex items-center justify-end gap-3">
          <button
            type="button"
            class="px-5 py-2.5 rounded-full border border-gray-300 text-gray-700 font-['Raleway',sans-serif] font-bold text-sm hover:bg-gray-50 transition-colors cursor-pointer bg-white"
            :disabled="isArchivingProgramme"
            @click.prevent="programmeToArchive = null"
          >
            Cancel
          </button>
          <button
            type="button"
            class="flex items-center justify-center px-5 py-2.5 rounded-full bg-[#01d680] text-[#043f42] hover:bg-[#00c270] font-['Raleway',sans-serif] font-bold text-sm transition-colors border-none cursor-pointer shadow-sm disabled:opacity-50"
            :disabled="isArchivingProgramme"
            @click.prevent="submitArchiveProgramme"
          >
            <span
              v-if="isArchivingProgramme"
              class="w-4 h-4 mr-2 border-2 border-[#043f42] border-t-transparent rounded-full animate-spin"
            />
            {{ isArchivingProgramme ? 'Archiving...' : 'Confirm Archive' }}
          </button>
        </div>
      </div>
      <form
        method="dialog"
        class="modal-backdrop bg-[#00000099]"
      >
        <button
          :disabled="isArchivingProgramme"
          @click.prevent="!isArchivingProgramme && (programmeToArchive = null)"
        >
          close
        </button>
      </form>
    </dialog>

    <!-- Mobile Floating Action Button (FAB, consistent with Dashboard & Cohorts) -->
    <div
      v-if="!cohort.archived_at"
      id="cohort-detail-mobile-fab-container"
      class="fixed bottom-[74px] right-4 sm:right-6 z-40 lg:hidden"
    >
      <button
        type="button"
        aria-label="Add new programme"
        class="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#01d680] hover:bg-[#00c270] text-[#043f42] shadow-xl hover:shadow-2xl flex items-center justify-center transition-all duration-200 cursor-pointer focus:outline-none focus:ring-4 focus:ring-[#01d680]/30 active:scale-95"
        @click="openAddProgrammeModal"
      >
        <svg
          class="w-6 h-6 transition-transform duration-200"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M12 4.5v15m7.5-7.5h-15"
          />
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
import ProgrammeCard from './ProgrammeCard.vue'
import AddProgrammeModal from './AddProgrammeModal.vue'
import ArchivedCohortBanner from './ArchivedCohortBanner.vue'

export default {
  name: 'CohortDetail',
  components: {
    ProgrammeCard,
    AddProgrammeModal,
    ArchivedCohortBanner
  },
  props: {
    cohort: {
      type: Object,
      required: true
    },
    initialFilter: {
      type: String,
      default: 'all'
    }
  },
  data () {
    const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null
    const urlFilter = urlParams ? urlParams.get('status') : null
    const urlPage = urlParams ? parseInt(urlParams.get('page'), 10) : 1

    return {
      activeFilter: urlFilter || this.initialFilter || 'all',
      searchQuery: '',
      isFilterSortOpen: false,
      currentSort: 'date-desc',
      currentPage: urlPage > 0 ? urlPage : 1,
      perPage: 10,
      showAddProgrammeModal: false,
      programmeToDelete: null,
      isDeletingProgramme: false,
      isProgrammeDeleteSuccess: false,
      programmeDeleteProgress: 0,
      programmeDeleteStatusText: 'Deleting...',
      deleteProgrammeError: null,
      programmeToClone: null,
      cloneProgrammeName: '',
      isCloningProgramme: false,
      cloneProgrammeError: null,
      programmeToArchive: null,
      isArchivingProgramme: false,
      archiveProgrammeError: null
    }
  },
  computed: {
    cohortSetaType () {
      if (this.cohort && this.cohort.seta_type && this.cohort.seta_type !== 'SETA') {
        return this.cohort.seta_type
      }
      if (this.cohort && this.cohort.programmes && this.cohort.programmes.length > 0) {
        return this.cohort.programmes[0].seta_type
      }
      return null
    },
    allProgrammes () {
      return this.cohort.programmes || []
    },
    filteredProgrammes () {
      let list = this.allProgrammes

      // Filter by status
      if (this.activeFilter === 'drafts' || this.activeFilter === 'draft') {
        list = list.filter(p => {
          const st = (p.derived_status || p.status || '').toLowerCase()
          return st === 'draft'
        })
      } else if (this.activeFilter === 'in_progress') {
        list = list.filter(p => {
          const st = (p.derived_status || p.status || '').toLowerCase()
          return st === 'in_progress' || st === 'active' || st === 'generating' || st === 'ready'
        })
      } else if (this.activeFilter === 'completed') {
        list = list.filter(p => {
          const st = (p.derived_status || p.status || '').toLowerCase()
          return st === 'completed'
        })
      }

      // Filter by search query
      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase().trim()
        list = list.filter(p => {
          const name = (p.name || '').toLowerCase()
          const sponsorName = (p.sponsor_name || '').toLowerCase()
          const sponsorEmail = (p.sponsor_email || '').toLowerCase()
          const qual = (p.qualification_id || '').toLowerCase()
          const seta = (p.seta_type || '').toLowerCase()
          return name.includes(q) || sponsorName.includes(q) || sponsorEmail.includes(q) || qual.includes(q) || seta.includes(q)
        })
      }

      // Sort programmes
      const sorted = [...list]
      if (this.currentSort === 'name-asc') {
        sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      } else if (this.currentSort === 'name-desc') {
        sorted.sort((a, b) => (b.name || '').localeCompare(a.name || ''))
      } else if (this.currentSort === 'date-asc') {
        sorted.sort((a, b) => new Date(a.created_at || a.last_activity || 0) - new Date(b.created_at || b.last_activity || 0))
      } else {
        sorted.sort((a, b) => new Date(b.created_at || b.last_activity || 0) - new Date(a.created_at || a.last_activity || 0))
      }

      return sorted
    },
    totalPages () {
      return Math.ceil(this.filteredProgrammes.length / this.perPage) || 1
    },
    paginatedProgrammes () {
      const start = (this.currentPage - 1) * this.perPage
      return this.filteredProgrammes.slice(start, start + this.perPage)
    },
    paginationFrom () {
      if (this.filteredProgrammes.length === 0) return 0
      return (this.currentPage - 1) * this.perPage + 1
    },
    paginationTo () {
      return Math.min(this.currentPage * this.perPage, this.filteredProgrammes.length)
    },
    filterEmptyTitle () {
      if (this.searchQuery.trim()) {
        return 'No matching programmes found'
      }
      if (this.activeFilter === 'drafts' || this.activeFilter === 'draft') {
        return 'No draft programmes yet'
      }
      if (this.activeFilter === 'in_progress') {
        return 'No in-progress programmes yet'
      }
      if (this.activeFilter === 'completed') {
        return 'No completed programmes yet'
      }
      return 'No programmes found'
    },
    filterEmptySubtitle () {
      if (this.searchQuery.trim()) {
        return `No programmes matched "${this.searchQuery}".`
      }
      return 'There are currently no programmes in this view.'
    }
  },
  mounted () {
    window.addEventListener('popstate', this.syncUrlState)
    this.handleOutsideClick = () => {
      this.closeFilterSort()
    }
    document.addEventListener('click', this.handleOutsideClick)
  },
  beforeUnmount () {
    window.removeEventListener('popstate', this.syncUrlState)
    if (this.handleOutsideClick) {
      document.removeEventListener('click', this.handleOutsideClick)
    }
    if (this.deleteTimer) clearInterval(this.deleteTimer)
    if (this.deleteReloadTimeout) clearTimeout(this.deleteReloadTimeout)
  },
  methods: {
    pillClass (filter) {
      let isActive = this.activeFilter === filter
      if (filter === 'drafts' && (this.activeFilter === 'draft' || this.activeFilter === 'drafts')) {
        isActive = true
      }
      const base = 'flex flex-row gap-[8px] sm:gap-[10px] px-3.5 sm:px-[16px] py-2 sm:py-[10px] justify-center items-center rounded-full cursor-pointer transition-colors shrink-0 whitespace-nowrap'
      if (isActive) {
        return `${base} border border-black bg-transparent`
      } else {
        return `${base} border border-transparent bg-[#9b9b9b]/25 hover:bg-[#9b9b9b]/40`
      }
    },
    setFilter (filter) {
      this.activeFilter = filter
      this.currentPage = 1
      this.updateUrl()
    },
    setPage (page) {
      if (page < 1 || page > this.totalPages) return
      this.currentPage = page
      this.updateUrl()
    },
    clearSearch () {
      this.searchQuery = ''
      this.currentPage = 1
    },
    resetFilters () {
      this.activeFilter = 'all'
      this.searchQuery = ''
      this.currentPage = 1
      this.updateUrl()
    },
    updateUrl () {
      if (typeof window === 'undefined') return
      const url = new URL(window.location.href)
      if (this.activeFilter === 'all') {
        url.searchParams.delete('status')
      } else {
        url.searchParams.set('status', this.activeFilter)
      }
      if (this.currentPage > 1) {
        url.searchParams.set('page', this.currentPage)
      } else {
        url.searchParams.delete('page')
      }
      window.history.pushState({}, '', url.toString())
    },
    syncUrlState () {
      if (typeof window === 'undefined') return
      const urlParams = new URLSearchParams(window.location.search)
      this.activeFilter = urlParams.get('status') || 'all'
      const pageParam = parseInt(urlParams.get('page'), 10)
      this.currentPage = pageParam > 0 ? pageParam : 1
    },
    toggleFilterSort () {
      this.isFilterSortOpen = !this.isFilterSortOpen
    },
    closeFilterSort () {
      this.isFilterSortOpen = false
    },
    setSort (sort) {
      this.currentSort = sort
      this.isFilterSortOpen = false
      this.currentPage = 1
    },
    openAddProgrammeModal () {
      this.showAddProgrammeModal = true
    },
    onProgrammeCreated (programme) {
      if (!this.cohort.programmes) {
        this.cohort.programmes = []
      }
      this.cohort.programmes.unshift(programme)
    },
    onProgrammeAction (payload) {
      if (payload.action === 'delete') {
        this.programmeToDelete = payload.programme || (this.cohort.programmes || []).find(p => p.id === payload.programme_id) || { id: payload.programme_id }
        this.deleteProgrammeError = null
      } else if (payload.action === 'clone') {
        const prog = payload.programme || (this.cohort.programmes || []).find(p => p.id === payload.programme_id)
        if (prog) {
          this.programmeToClone = prog
          this.cloneProgrammeName = `${(prog.name || `Programme #${prog.id}`).replace(/\s*\(Copy(?:\s+\d+)?\)$/, '')} (Copy)`
          this.cloneProgrammeError = null
        }
      } else if (payload.action === 'archive') {
        const prog = payload.programme || (this.cohort.programmes || []).find(p => p.id === payload.programme_id)
        if (prog) {
          this.programmeToArchive = prog
          this.archiveProgrammeError = null
        }
      }
    },
    async submitCloneProgramme () {
      if (!this.programmeToClone) return
      this.isCloningProgramme = true
      this.cloneProgrammeError = null

      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        const response = await fetch(`/cohorts/${this.cohort.id}/programmes/${this.programmeToClone.id}/clone.json`, {
          method: 'POST',
          headers: {
            'X-CSRF-Token': csrfToken,
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            programme: {
              name: this.cloneProgrammeName
            }
          })
        })

        const data = await response.json().catch(() => ({}))

        if (response.ok && data.success) {
          if (data.programme) {
            if (!this.cohort.programmes) this.cohort.programmes = []
            this.cohort.programmes.unshift(data.programme)
          }
          this.currentPage = 1
          this.programmeToClone = null
          if (window.showFlash) {
            window.showFlash('notice', 'Programme cloned successfully.')
          }
        } else {
          this.cloneProgrammeError = (data.errors && data.errors.join(', ')) || data.error || 'Failed to clone programme.'
        }
      } catch (err) {
        this.cloneProgrammeError = 'A network error occurred. Please try again.'
      } finally {
        this.isCloningProgramme = false
      }
    },
    async submitArchiveProgramme () {
      if (!this.programmeToArchive) return
      this.isArchivingProgramme = true
      this.archiveProgrammeError = null

      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        const response = await fetch(`/cohorts/${this.cohort.id}/programmes/${this.programmeToArchive.id}/archive.json`, {
          method: 'POST',
          headers: {
            'X-CSRF-Token': csrfToken,
            'Content-Type': 'application/json',
            Accept: 'application/json'
          }
        })

        const data = await response.json().catch(() => ({}))

        if (response.ok && data.success) {
          const target = (this.cohort.programmes || []).find(p => p.id === this.programmeToArchive.id)
          if (target) {
            target.status = 'archived'
            target.derived_status = 'archived'
          }
          this.programmeToArchive = null
          if (window.showFlash) {
            window.showFlash('notice', 'Programme archived successfully.')
          }
        } else {
          this.archiveProgrammeError = (data.errors && data.errors.join(', ')) || data.error || 'Failed to archive programme.'
        }
      } catch (err) {
        this.archiveProgrammeError = 'A network error occurred. Please try again.'
      } finally {
        this.isArchivingProgramme = false
      }
    },
    async submitDeleteProgramme () {
      if (!this.programmeToDelete) return
      this.isDeletingProgramme = true
      this.isProgrammeDeleteSuccess = false
      this.deleteProgrammeError = null
      this.programmeDeleteProgress = 30
      this.programmeDeleteStatusText = 'Deleting...'

      if (this.deleteTimer) clearInterval(this.deleteTimer)
      this.deleteTimer = setInterval(() => {
        if (this.programmeDeleteProgress < 85 && !this.isProgrammeDeleteSuccess) {
          this.programmeDeleteProgress += 20
        }
      }, 150)

      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

        const response = await fetch(`/cohorts/${this.cohort.id}/programmes/${this.programmeToDelete.id}.json`, {
          method: 'DELETE',
          headers: {
            'X-CSRF-Token': csrfToken,
            'Content-Type': 'application/json',
            Accept: 'application/json'
          }
        })

        const data = await response.json().catch(() => ({}))

        if (response.ok && data.success) {
          this.programmeDeleteProgress = 100
          this.isProgrammeDeleteSuccess = true
          this.programmeDeleteStatusText = 'Programme deleted.'
          this.deleteReloadTimeout = setTimeout(() => {
            this.finishProgrammeDelete()
          }, 900)
        } else {
          this.deleteProgrammeError = data.error || 'Failed to delete programme.'
          this.isDeletingProgramme = false
        }
      } catch (err) {
        this.deleteProgrammeError = 'A network error occurred. Please try again.'
        this.isDeletingProgramme = false
      } finally {
        if (this.deleteTimer) {
          clearInterval(this.deleteTimer)
          this.deleteTimer = null
        }
      }
    },
    finishProgrammeDelete () {
      if (this.deleteReloadTimeout) {
        clearTimeout(this.deleteReloadTimeout)
        this.deleteReloadTimeout = null
      }
      if (this.programmeToDelete) {
        this.cohort.programmes = (this.cohort.programmes || []).filter(p => p.id !== this.programmeToDelete.id)
        this.programmeToDelete = null
      }
      window.location.reload()
    }
  }
}
</script>
